import { emitBlock } from "../../ls/emit-block.js";
import { parseLsBlocks, type LsBlock } from "../../ls/parse-blocks.js";
import { deleteBlockByLineWithResult, insertBlockNearLineWithResult, moveLineRangeRelative, type DeleteBlockResult, type InsertBlockResult, type MoveBlockResult } from "../editor/card-ops.js";
import { defaultChoiceRouteTemplate, templateFor, type InsertKind } from "../editor/block-templates.js";
import { isStoryboardKind, type PresentationStep } from "./presentation-model.js";
import { blockLineRange, deleteLines, insertLine, type LineRange } from "../../ls/edit-source.js";

export interface PresentationStepFields {
  readonly branchKey?: string;
  readonly title?: string;
  readonly name?: string;
  readonly transition?: string;
  readonly character?: string;
  readonly pose?: string;
  readonly bubble?: boolean;
  readonly speaker?: string;
  readonly text?: string;
  readonly content?: string;
  readonly stop?: boolean;
  readonly id?: string;
  readonly mode?: "brave" | "safe";
  readonly delta?: string;
  readonly signalKind?: "mark" | "int";
  readonly op?: string;
  readonly value?: string;
  readonly trickType?: string;
  readonly prompt?: string;
  readonly direction?: "from" | "to";
  readonly description?: string;
  readonly key?: string;
  readonly next?: string;
  readonly end?: string;
  readonly condition?: string;
}

const PATCHABLE_FIELDS: Partial<Record<LsBlock["kind"], readonly (keyof PresentationStepFields)[]>> = {
  episode: ["branchKey", "title"],
  bg: ["name", "transition"],
  char: ["character", "pose", "transition", "bubble"],
  dialogue: ["speaker", "text", "pose"],
  narration: ["speaker", "text"],
  cg: ["name", "content"],
  music: ["name", "stop"],
  sfx: ["name"],
  pause: [],
  option: ["id", "mode", "text"],
  affection: ["character", "delta"],
  signal: ["signalKind", "name", "op", "value"],
  trick: ["trickType", "prompt"],
  phoneText: ["direction", "character", "text"],
  minigame: ["name", "description"],
  achievement: ["id"],
  butterfly: ["description"],
  param: ["key", "value"],
  gate: ["next", "end"],
  if: ["condition", "next", "end"],
};

const ATTACHED_TOOL_KINDS = new Set<LsBlock["kind"]>([
  "affection",
  "signal",
  "butterfly",
  "achievement",
  "param",
]);

function hasImmediateStoryboardAnchor(blocks: readonly LsBlock[], index: number, depth: number): boolean {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const candidate = blocks[cursor]!;
    if (candidate.depth > depth) continue;
    if (candidate.depth < depth) return false;
    if (candidate.concurrent && isStoryboardKind(candidate.kind)) continue;
    return !candidate.concurrent && isStoryboardKind(candidate.kind);
  }
  return false;
}

function replaceLinePreservingIndent(source: string, line: number, replacement: string): string {
  const lines = source.split(/\r?\n/);
  const index = line - 1;
  if (index < 0 || index >= lines.length) return source;
  const indent = lines[index]?.match(/^\s*/)?.[0] ?? "";
  lines[index] = `${indent}${replacement}`;
  return lines.join("\n");
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renameChoiceHistoryReferences(source: string, from: string, to: string): string {
  const reference = new RegExp(`\\b${escapeRegExp(from)}\\.(success|fail|any)\\b`, "g");
  return source.replace(reference, `${to}.$1`);
}

export function patchPresentationStep(
  source: string,
  step: PresentationStep,
  fields: PresentationStepFields,
): string {
  const allowedFields = PATCHABLE_FIELDS[step.kind];
  if (!step.editable || allowedFields === undefined) return source;

  const patch: Record<string, unknown> = {};
  for (const field of allowedFields) {
    if (Object.prototype.hasOwnProperty.call(fields, field)) patch[field] = fields[field];
  }
  if (step.kind === "option" && step.block.kind === "option") {
    const requestedId = typeof patch.id === "string" ? patch.id.trim() : undefined;
    if (requestedId && requestedId !== step.block.id) {
      const duplicate = parseLsBlocks(source).some((block) => block.kind === "option" && block.line !== step.line && block.id === requestedId);
      if (duplicate) delete patch.id;
    }
    const requestedMode = patch.mode === "brave" || patch.mode === "safe" ? patch.mode : undefined;
    if (requestedMode && requestedMode !== step.block.mode) {
      const blocks = parseLsBlocks(source);
      const index = blocks.findIndex((block) => block.line === step.line);
      const range = index >= 0 ? blockLineRange(source, blocks, index) : undefined;
      if (!range) return source;
      const lines = source.split(/\r?\n/);
      const indent = lines[step.line - 1]?.match(/^\s*/)?.[0] ?? "";
      const id = (typeof patch.id === "string" && patch.id.trim()) || step.block.id || "A";
      const text = (typeof patch.text === "string" ? patch.text : step.block.text) || "新选项";
      const inner = `${indent}  `;
      const optionChildren = blocks.filter((block) => block.line > range.start && block.line < range.end);
      const directCheckIndex = blocks.findIndex((block) => block.kind === "check" && block.depth === step.depth + 1 && block.line > range.start && block.line < range.end);
      if (requestedMode === "safe" && optionChildren.some((block) => block.kind === "if" && block.condition?.includes("check."))) {
        // A lossless automatic conversion is impossible: choosing one branch
        // would delete the other. Refuse instead of silently destroying prose.
        return source;
      }
      lines[range.start - 1] = `${indent}@option ${id} ${requestedMode} "${text}" {`;
      if (requestedMode === "brave" && directCheckIndex < 0) {
        lines.splice(range.start, 0, `${inner}check {`, `${inner}  attr: CHA`, `${inner}  dc: 12`, `${inner}}`);
      } else if (requestedMode === "safe" && directCheckIndex >= 0) {
        const checkRange = blockLineRange(source, blocks, directCheckIndex);
        lines.splice(checkRange.start - 1, checkRange.end - checkRange.start + 1);
      }
      const next = lines.join("\n");
      return step.block.id && id !== step.block.id ? renameChoiceHistoryReferences(next, step.block.id, id) : next;
    }
  }
  if (Object.keys(patch).length === 0) return source;

  const updated = { ...step.block, ...patch } as LsBlock;
  let next = replaceLinePreservingIndent(source, step.line, emitBlock(updated));
  if (
    step.kind === "option" &&
    step.block.kind === "option" &&
    step.block.id &&
    typeof fields.id === "string" &&
    fields.id.length > 0 &&
    fields.id !== step.block.id
  ) {
    next = renameChoiceHistoryReferences(next, step.block.id, fields.id);
  }
  return next;
}

export function insertPresentationTemplate(
  source: string,
  kind: InsertKind,
  afterLine?: number | null,
): InsertBlockResult {
  const blocks = parseLsBlocks(source);
  if (kind === "gate" && blocks.some((block) => block.kind === "gate")) {
    return { source, insertedLine: null };
  }
  const usedOptionIds = new Set(blocks.filter((block) => block.kind === "option").map((block) => block.kind === "option" ? block.id : undefined));
  const choiceIds = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").filter((id) => !usedOptionIds.has(id)).slice(0, 2);
  if (kind === "choice" && choiceIds.length < 2) return { source, insertedLine: null };
  const choiceIdMap = { A: choiceIds[0], B: choiceIds[1] } as const;
  const withChoiceIds = (template: string): string => kind !== "choice" ? template : template
    .replace(/@option ([AB])\b/g, (_match, id: "A" | "B") => `@option ${choiceIdMap[id]}`)
    .replace(/\b([AB])\.(success|fail|any)\b/g, (_match, id: "A" | "B", result: string) => `${choiceIdMap[id]}.${result}`);
  if (afterLine == null) {
    const episode = blocks.find((block) => block.kind === "episode");
    const firstContent = blocks.find((block) => block.kind !== "episode" && block.kind !== "gate");
    const insertAt = firstContent?.line ?? (episode ? episode.line + 1 : 1);
    const lines = source.split(/\r?\n/);
    const indent = firstContent
      ? (lines[firstContent.line - 1]?.match(/^\s*/)?.[0] ?? "")
      : episode
        ? `${lines[episode.line - 1]?.match(/^\s*/)?.[0] ?? ""}  `
        : "";
    const rendered = withChoiceIds(templateFor(kind)).split("\n").map((line) => line ? `${indent}${line}` : line).join("\n");
    let next = insertLine(source, insertAt, rendered);
    if (kind === "choice" && !blocks.some((block) => block.kind === "gate")) {
      const nextLines = next.split(/\r?\n/);
      let closeLine = nextLines.length + 1;
      for (let index = nextLines.length - 1; index >= 0; index -= 1) {
        if (nextLines[index]?.trim() === "}") { closeLine = index + 1; break; }
      }
      const route = withChoiceIds(defaultChoiceRouteTemplate()).split("\n").map((line) => line ? `${indent}${line}` : line).join("\n");
      next = insertLine(next, closeLine, route);
    }
    return { source: next, insertedLine: insertAt };
  }
  let anchorIndex = blocks.findIndex((block) => block.line === afterLine);
  const episodeDepth = blocks.find((block) => block.kind === "episode")?.depth;
  const frameDepth = episodeDepth == null
    ? Math.min(...blocks.filter((block) => block.kind !== "episode").map((block) => block.depth))
    : episodeDepth + 1;
  if (anchorIndex >= 0 && Number.isFinite(frameDepth) && blocks[anchorIndex]!.depth > frameDepth) {
    for (let index = anchorIndex - 1; index >= 0; index -= 1) {
      if (blocks[index]!.depth === frameDepth) {
        anchorIndex = index;
        break;
      }
    }
  }
  let anchorLine = afterLine;
  if (anchorIndex >= 0) {
    const anchor = blocks[anchorIndex]!;
    anchorLine = anchor.line;
    for (let index = anchorIndex + 1; index < blocks.length; index += 1) {
      const candidate = blocks[index]!;
      if (candidate.depth !== anchor.depth) break;
      if (!candidate.concurrent && !ATTACHED_TOOL_KINDS.has(candidate.kind)) break;
      anchorLine = candidate.line;
    }
  }
  if (kind === "choice") {
    const resolvedIndex = blocks.findIndex((block) => block.line === anchorLine);
    const anchor = blocks[resolvedIndex];
    if (!anchor) return { source, insertedLine: null };
    const range = blockLineRange(source, blocks, resolvedIndex);
    const lines = source.split(/\r?\n/);
    const indent = lines[anchor.line - 1]?.match(/^\s*/)?.[0] ?? "";
    const insertAt = range.end + 1;
    const rendered = withChoiceIds(templateFor(kind)).split("\n").map((line) => line ? `${indent}${line}` : line).join("\n");
    let next = insertLine(source, insertAt, rendered);
    if (!blocks.some((block) => block.kind === "gate")) {
      const nextLines = next.split(/\r?\n/);
      let closeLine = nextLines.length + 1;
      for (let index = nextLines.length - 1; index >= 0; index -= 1) {
        if (nextLines[index]?.trim() === "}") { closeLine = index + 1; break; }
      }
      const route = withChoiceIds(defaultChoiceRouteTemplate()).split("\n").map((line) => line ? `${indent}${line}` : line).join("\n");
      next = insertLine(next, closeLine, route);
    }
    return { source: next, insertedLine: insertAt };
  }
  return insertBlockNearLineWithResult(source, blocks, kind, anchorLine);
}

/** Insert staging directly into a composed frame as an `&` follower. */
export function insertPresentationCompanion(
  source: string,
  kind: InsertKind,
  afterLine: number,
): InsertBlockResult {
  const blocks = parseLsBlocks(source);
  const anchorIndex = blocks.findIndex((block) => block.line === afterLine);
  if (anchorIndex < 0) return { source, insertedLine: null };
  const anchor = blocks[anchorIndex]!;
  let anchorLine = anchor.line;
  for (let index = anchorIndex + 1; index < blocks.length; index += 1) {
    const candidate = blocks[index]!;
    if (candidate.depth !== anchor.depth || !candidate.concurrent) break;
    anchorLine = candidate.line;
  }
  const result = insertBlockNearLineWithResult(source, blocks, kind, anchorLine);
  if (result.source === source || result.insertedLine == null) return result;
  const inserted = parseLsBlocks(result.source).find((block) => block.line === result.insertedLine);
  if (!inserted || !CONCURRENT_KINDS.has(inserted.kind)) return result;
  const lines = result.source.split(/\r?\n/);
  const index = result.insertedLine - 1;
  const indent = lines[index]?.match(/^\s*/)?.[0] ?? "";
  lines[index] = `${indent}${emitBlock({ ...inserted, concurrent: true } as LsBlock)}`;
  return { source: lines.join("\n"), insertedLine: result.insertedLine };
}

const CONCURRENT_KINDS = new Set<LsBlock["kind"]>([
  "bg",
  "char",
  "cg",
  "music",
  "sfx",
  "pause",
  "affection",
  "signal",
  "trick",
  "phoneText",
  "minigame",
  "butterfly",
]);

/** Toggle Lunascript's `&` follower form for directives that support it. */
export function setPresentationStepConcurrent(
  source: string,
  step: PresentationStep,
  concurrent: boolean,
): string {
  if (!step.editable || !CONCURRENT_KINDS.has(step.kind)) return source;
  if (Boolean(step.concurrent) === concurrent) return source;
  if (concurrent) {
    const blocks = parseLsBlocks(source);
    const index = blocks.findIndex((block) => block.line === step.line);
    const hasPreviousFrame = index > 0 && hasImmediateStoryboardAnchor(blocks, index, step.depth);
    if (!hasPreviousFrame) return source;
  }
  return replaceLinePreservingIndent(
    source,
    step.line,
    emitBlock({ ...step.block, concurrent } as LsBlock),
  );
}

export interface ReplacePresentationResult extends InsertBlockResult {
  readonly changed: boolean;
}

const REPLACEABLE_SOURCE_KINDS = new Set<LsBlock["kind"]>([
  "bg",
  "char",
  "dialogue",
  "narration",
  "cg",
  "music",
  "sfx",
  "pause",
  "affection",
  "signal",
  "trick",
  "phoneText",
  "minigame",
  "butterfly",
]);

/**
 * Replace a leaf frame in place. Structural owners (episode/choice/option/gate)
 * are deliberately protected because a line-only replacement could orphan a
 * body or route. The template is re-indented to the selected frame's depth.
 */
export function replacePresentationStepTemplate(
  source: string,
  step: PresentationStep,
  kind: InsertKind,
): ReplacePresentationResult {
  if (!REPLACEABLE_SOURCE_KINDS.has(step.kind)) {
    return { source, insertedLine: null, changed: false };
  }
  const blocks = parseLsBlocks(source);
  const range = frameRange(source, blocks, step.line);
  if (!range) return { source, insertedLine: null, changed: false };
  const lines = source.split(/\r?\n/);
  const index = range.start - 1;
  const current = lines[index];
  if (current == null) return { source, insertedLine: null, changed: false };
  const indent = current.match(/^\s*/)?.[0] ?? "";
  const templateLines = templateFor(kind).split("\n");
  const targetBlock = parseLsBlocks(templateFor(kind))[0];
  if (step.concurrent && targetBlock && CONCURRENT_KINDS.has(targetBlock.kind)) {
    templateLines[0] = templateLines[0]!.replace(/^@/, "&");
  }
  const replacement = templateLines
    .map((line) => (line ? `${indent}${line}` : line))
    .join("\n");
  lines.splice(index, range.end - range.start + 1, replacement);
  const next = lines.join("\n");
  return { source: next, insertedLine: range.start, changed: next !== source };
}

function frameRange(source: string, blocks: readonly LsBlock[], line: number): LineRange | undefined {
  const index = blocks.findIndex((block) => block.line === line);
  const block = blocks[index];
  if (!block || block.kind === "episode" || block.kind === "gate") return undefined;
  const range = blockLineRange(source, blocks, index);
  if (block.concurrent) return range;
  let end = range.end;
  for (let cursor = index + 1; cursor < blocks.length; cursor += 1) {
    const candidate = blocks[cursor]!;
    if (candidate.depth !== block.depth || !candidate.concurrent) break;
    end = blockLineRange(source, blocks, cursor).end;
  }
  return { start: range.start, end };
}

function moveRange(source: string, blocks: readonly LsBlock[], line: number): LineRange | undefined {
  const base = frameRange(source, blocks, line);
  if (!base) return undefined;
  const selected = blocks.find((block) => block.line === line);
  if (!selected || selected.concurrent) return base;
  let end = base.end;
  for (const candidate of blocks) {
    if (candidate.line <= base.end) continue;
    if (candidate.depth !== selected.depth || !ATTACHED_TOOL_KINDS.has(candidate.kind)) break;
    const index = blocks.indexOf(candidate);
    end = blockLineRange(source, blocks, index).end;
  }
  return { start: base.start, end };
}

/** Delete a visual frame and all of its folded `&` staging followers. */
export function deletePresentationFrame(source: string, line: number): DeleteBlockResult {
  const blocks = parseLsBlocks(source);
  const selected = blocks.find((block) => block.line === line);
  if (!selected) return { source, deletedLine: null, changed: false };
  // Choice deletion also cleans exact route references; keep the mature card op.
  if (selected.kind === "choice" || selected.kind === "option" || selected.kind === "gate") {
    return deleteBlockByLineWithResult(source, blocks, line);
  }
  const range = frameRange(source, blocks, line);
  if (!range) return { source, deletedLine: null, changed: false };
  const next = deleteLines(source, range.start, range.end);
  return { source: next, deletedLine: line, changed: next !== source };
}

/** Move a composed frame as one unit so background/music/character never split. */
export function movePresentationFrame(
  source: string,
  fromLine: number,
  toLine: number,
  placement: "before" | "after",
): MoveBlockResult {
  const blocks = parseLsBlocks(source);
  const from = moveRange(source, blocks, fromLine);
  const to = moveRange(source, blocks, toLine);
  if (!from || !to) return { source, movedLine: null, changed: false };
  const next = moveLineRangeRelative(source, from, to, placement);
  const movedLength = from.end - from.start + 1;
  const movedLine = from.start < to.start
    ? (placement === "after" ? to.end - movedLength + 1 : to.start - movedLength)
    : (placement === "before" ? to.start : to.end + 1);
  return { source: next, movedLine, changed: next !== source };
}

/** Add a simple safe option before a choice's closing brace. */
export function addPresentationChoiceOption(source: string, choiceLine: number): InsertBlockResult {
  const blocks = parseLsBlocks(source);
  const index = blocks.findIndex((block) => block.line === choiceLine && block.kind === "choice");
  const choice = blocks[index];
  if (!choice) return { source, insertedLine: null };
  const range = blockLineRange(source, blocks, index);
  const ids = new Set(blocks.filter((block) => block.kind === "option").map((block) => block.kind === "option" ? block.id : undefined));
  const id = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").find((candidate) => !ids.has(candidate));
  if (!id) return { source, insertedLine: null };
  const choiceIndent = source.split(/\r?\n/)[choice.line - 1]?.match(/^\s*/)?.[0] ?? "";
  const indent = `${choiceIndent}  `;
  const template = [`${indent}@option ${id} safe "新选项" {`, `${indent}  YOU: ……`, `${indent}}`].join("\n");
  return { source: insertLine(source, range.end, template), insertedLine: range.end };
}

/** Delete one option while keeping at least two understandable choices. */
export function deletePresentationChoiceOption(source: string, optionLine: number): DeleteBlockResult {
  const blocks = parseLsBlocks(source);
  const option = blocks.find((block) => block.line === optionLine && block.kind === "option");
  if (!option) return { source, deletedLine: null, changed: false };
  let parentChoice: LsBlock | undefined;
  for (let index = blocks.findIndex((block) => block.line === optionLine) - 1; index >= 0; index -= 1) {
    if (blocks[index]!.depth < option.depth) { parentChoice = blocks[index]; break; }
  }
  if (!parentChoice || parentChoice.kind !== "choice") return { source, deletedLine: null, changed: false };
  const choiceIndex = blocks.indexOf(parentChoice);
  const range = blockLineRange(source, blocks, choiceIndex);
  const optionCount = blocks.filter((block) => block.kind === "option" && block.depth === parentChoice!.depth + 1 && block.line > range.start && block.line < range.end).length;
  if (optionCount <= 2) return { source, deletedLine: null, changed: false };
  return deleteBlockByLineWithResult(source, blocks, optionLine);
}
